// Copyright (c) 2026 Aravind Adari. All rights reserved.

import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  CircularProgress,
  TablePagination,
  Divider,
  InputAdornment,
  Switch,
  FormControlLabel,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import BusinessIcon from "@mui/icons-material/Business";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SettingsIcon from "@mui/icons-material/Settings";
import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  setDoc,
  query,
  orderBy,
  Timestamp,
  getDoc,
  limit,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "../firebase";
import dayjs from "dayjs";
import { timed, perfTotal } from "../utils/perf";
import SummaryCard from "../components/SummaryCard";

// ── Types ──────────────────────────────────────────────────────────────────

interface ClientFeatures {
  payroll: boolean;
  leaves: boolean;
  reports: boolean;
  advances: boolean;
}

interface Client {
  id: string;
  name: string;
  slug: string;
  status: "active" | "suspended" | "trial";
  plan: "starter" | "pro" | "enterprise";
  industry: string;
  timezone: string;
  contactEmail: string;
  contactPhone: string;
  address: string;
  createdAt?: Timestamp;
  employeeCount?: number;
  branchCount?: number;
  // Configuration fields
  timesheetEditDays?: number;
  employeeLimit?: number;
  branchLimit?: number;
  features?: ClientFeatures;
}

interface ClientConfig {
  timesheetEditDays: number;
  employeeLimit: number;
  branchLimit: number;
  features: ClientFeatures;
}


interface ClientUser {
  uid: string;
  email: string;
  displayName?: string;
  clientId: string;
  clientName: string;
  role: string;
  disabled: boolean;
  createdAt?: string;
}

interface PlatformAuditEntry {
  id: string;
  action: string;
  clientId: string;
  performedBy: string;
  details: Record<string, unknown>;
  timestamp?: Timestamp;
}

const STATUS_COLORS: Record<string, "success" | "warning" | "error" | "default"> = {
  active: "success",
  trial: "warning",
  suspended: "error",
};

const PLAN_COLORS: Record<string, "default" | "primary" | "secondary"> = {
  starter: "default",
  pro: "primary",
  enterprise: "secondary",
};

const emptyClientForm = (): Omit<Client, "id" | "createdAt" | "employeeCount" | "branchCount"> => ({
  name: "",
  slug: "",
  status: "trial",
  plan: "starter",
  industry: "",
  timezone: "Asia/Kolkata",
  contactEmail: "",
  contactPhone: "",
  address: "",
});

// ── Platform audit log helper ─────────────────────────────────────────────

async function logPlatformAudit(
  action: string,
  clientId: string,
  performedBy: string,
  details: Record<string, unknown>
) {
  try {
    await addDoc(collection(db, "platformAuditLog"), {
      action,
      clientId,
      performedBy,
      details,
      timestamp: Timestamp.now(),
    });
  } catch { /* non-fatal */ }
}

// ── Main Page Component ───────────────────────────────────────────────────

export default function PlatformPage() {
  const [tab, setTab] = useState(0);
  const [snack, setSnack] = useState<{ open: boolean; msg: string; severity: "success" | "error" }>({
    open: false, msg: "", severity: "success",
  });
  const toast = (msg: string, severity: "success" | "error" = "success") =>
    setSnack({ open: true, msg, severity });

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 800 }}>Platform Admin</Typography>
        <Typography variant="body2" color="text.secondary">
          Manage all clients, users, and platform-level configuration
        </Typography>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab label="Overview" />
        <Tab label="Clients" />
        <Tab label="Users" />
        <Tab label="Data Explorer" />
        <Tab label="Audit Log" />
      </Tabs>

      {tab === 0 && <OverviewTab toast={toast} />}
      {tab === 1 && <ClientsTab toast={toast} />}
      {tab === 2 && <UsersTab toast={toast} />}
      {tab === 3 && <DataExplorerTab />}
      {tab === 4 && <AuditLogTab />}

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// ── Overview Tab ──────────────────────────────────────────────────────────

function OverviewTab({ toast }: { toast: (m: string, s?: "success" | "error") => void }) {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalEmployees, setTotalEmployees] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    const t0 = performance.now();
    try {
      const snap = await timed("PlatformPage.overview.clients", () => getDocs(collection(db, "clients")));
      const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Client, "id">) }));
      setClients(list);

      // Count employees across all clients in parallel
      const empCounts = await Promise.all(
        list.map((c) =>
          getDocs(collection(db, "clients", c.id, "employees"))
            .then((s) => s.size)
            .catch(() => 0)
        )
      );
      setTotalEmployees(empCounts.reduce((sum, n) => sum + n, 0));
      perfTotal("PlatformPage.overview TOTAL", t0);
    } catch (e: unknown) {
      toast("Failed to load overview", "error");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const active = clients.filter((c) => c.status === "active").length;
  const trial = clients.filter((c) => c.status === "trial").length;
  const suspended = clients.filter((c) => c.status === "suspended").length;

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <SummaryCard label="Total Clients" value={loading ? "…" : clients.length} color="#1565C0" />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <SummaryCard label="Active" value={loading ? "…" : active} color="#2E7D32" />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <SummaryCard label="Trial" value={loading ? "…" : trial} color="#F9A825" />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <SummaryCard label="Total Employees" value={loading ? "…" : totalEmployees} color="#1565C0" />
        </Grid>
      </Grid>

      {suspended > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {suspended} client{suspended > 1 ? "s are" : " is"} currently suspended.
        </Alert>
      )}

      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>All Clients</Typography>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress /></Box>
      ) : clients.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <BusinessIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
          <Typography color="text.secondary">No clients yet. Add your first client in the Clients tab.</Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {clients.map((c) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={c.id}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{c.name}</Typography>
                    <Chip label={c.status} size="small" color={STATUS_COLORS[c.status]} />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    {c.slug} · {c.industry || "—"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    {c.contactEmail || "—"}
                  </Typography>
                  <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
                    <Chip label={c.plan} size="small" color={PLAN_COLORS[c.plan]} variant="outlined" />
                    <Chip label={c.timezone} size="small" variant="outlined" />
                  </Box>
                  {c.createdAt && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                      Since {dayjs(c.createdAt.toDate()).format("DD/MMM/YYYY")}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}

// ── Clients Tab ───────────────────────────────────────────────────────────

function ClientsTab({ toast }: { toast: (m: string, s?: "success" | "error") => void }) {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyClientForm());
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<Client | null>(null);
  const [expandedClient, setExpandedClient] = useState<string | null>(null);
  const [clientStats, setClientStats] = useState<Record<string, { employees: number; branches: number }>>({});
  const [configureClient, setConfigureClient] = useState<Client | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, "clients"), orderBy("createdAt", "desc")));
      setClients(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Client, "id">) })));
    } catch {
      toast("Failed to load clients", "error");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const loadClientStats = async (clientId: string) => {
    if (clientStats[clientId]) return;
    try {
      const [empSnap, brSnap] = await Promise.all([
        getDocs(collection(db, "clients", clientId, "employees")),
        getDocs(collection(db, "clients", clientId, "branches")),
      ]);
      setClientStats((prev) => ({ ...prev, [clientId]: { employees: empSnap.size, branches: brSnap.size } }));
    } catch { /* skip */ }
  };

  const openAdd = () => {
    setEditId(null);
    setForm(emptyClientForm());
    setDialogOpen(true);
  };

  const openEdit = (c: Client) => {
    setEditId(c.id);
    setForm({
      name: c.name,
      slug: c.slug,
      status: c.status,
      plan: c.plan,
      industry: c.industry || "",
      timezone: c.timezone || "Asia/Kolkata",
      contactEmail: c.contactEmail || "",
      contactPhone: c.contactPhone || "",
      address: c.address || "",
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast("Client name is required", "error"); return; }
    if (!form.slug.trim()) { toast("Slug is required", "error"); return; }
    if (!/^[a-z0-9_-]+$/.test(form.slug)) { toast("Slug must be lowercase letters, numbers, hyphens, underscores only", "error"); return; }

    setSaving(true);
    try {
      const data = {
        ...form,
        name: form.name.trim(),
        slug: form.slug.trim().toLowerCase(),
        updatedAt: Timestamp.now(),
      };

      if (editId) {
        await updateDoc(doc(db, "clients", editId), data);
        await logPlatformAudit("client-updated", editId, "super_admin", { name: data.name });
        toast("Client updated");
      } else {
        // Use slug as document ID for human-readable paths
        const clientId = form.slug.trim().toLowerCase();
        const exists = await getDoc(doc(db, "clients", clientId));
        if (exists.exists()) { toast(`Client with slug "${clientId}" already exists`, "error"); setSaving(false); return; }
        await setDoc(doc(db, "clients", clientId), { ...data, createdAt: Timestamp.now() });
        // Initialize default config subcollection
        await setDoc(doc(db, "clients", clientId, "config", "global"), {
          globalHolidayAllowance: 2,
          roleHolidayMap: {},
          legalHoldEmpIds: [],
          createdAt: Timestamp.now(),
        });
        await logPlatformAudit("client-created", clientId, "super_admin", { name: data.name });
        toast("Client created successfully");
      }
      setDialogOpen(false);
      load();
    } catch (e: unknown) {
      toast("Save failed: " + (e instanceof Error ? e.message : "unknown error"), "error");
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (c: Client) => {
    const newStatus = c.status === "active" ? "suspended" : "active";
    try {
      await updateDoc(doc(db, "clients", c.id), { status: newStatus, updatedAt: Timestamp.now() });
      await logPlatformAudit("client-status-changed", c.id, "super_admin", { from: c.status, to: newStatus });
      toast(`Client ${newStatus === "active" ? "activated" : "suspended"}`);
      load();
    } catch {
      toast("Status update failed", "error");
    }
  };

  const deleteClient = async () => {
    if (!deleteConfirm) return;
    try {
      await deleteDoc(doc(db, "clients", deleteConfirm.id));
      await logPlatformAudit("client-deleted", deleteConfirm.id, "super_admin", { name: deleteConfirm.name });
      toast("Client deleted");
      setDeleteConfirm(null);
      load();
    } catch {
      toast("Delete failed", "error");
    }
  };

  const filtered = clients.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.slug.toLowerCase().includes(search.toLowerCase()) ||
      (c.contactEmail || "").toLowerCase().includes(search.toLowerCase())
  );
  const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 1 }}>
        <TextField
          size="small"
          placeholder="Search clients…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 220 }}
        />
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button size="small" startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openAdd}>Add Client</Button>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress /></Box>
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ "& th": { fontWeight: 700, bgcolor: "action.hover" } }}>
                  <TableCell>Client Name</TableCell>
                  <TableCell>Slug / ID</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Plan</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginated.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={99} align="center" sx={{ py: 3, color: "text.secondary" }}>
                      No clients found
                    </TableCell>
                  </TableRow>
                ) : (
                  paginated.map((c) => (
                    <React.Fragment key={c.id}>
                      <TableRow
                        hover
                        sx={{ "& td": { verticalAlign: "middle" } }}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{c.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{c.industry || "—"}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ fontFamily: "monospace" }}>{c.slug}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={c.status} size="small" color={STATUS_COLORS[c.status]} />
                        </TableCell>
                        <TableCell>
                          <Chip label={c.plan} size="small" color={PLAN_COLORS[c.plan]} variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{c.contactEmail || "—"}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {c.createdAt ? dayjs(c.createdAt.toDate()).format("DD/MMM/YYYY") : "—"}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setExpandedClient(expandedClient === c.id ? null : c.id);
                                loadClientStats(c.id);
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => openEdit(c)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Configure">
                            <IconButton size="small" onClick={() => setConfigureClient(c)}>
                              <SettingsIcon fontSize="small" color="action" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={c.status === "active" ? "Suspend" : "Activate"}>
                            <IconButton size="small" onClick={() => toggleStatus(c)}>
                              {c.status === "active"
                                ? <PauseIcon fontSize="small" color="warning" />
                                : <PlayArrowIcon fontSize="small" color="success" />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => setDeleteConfirm(c)}>
                              <DeleteIcon fontSize="small" color="error" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                      {expandedClient === c.id && (
                        <TableRow>
                          <TableCell colSpan={99} sx={{ bgcolor: "action.hover", px: 3, py: 2 }}>
                            <ClientDetailPanel client={c} stats={clientStats[c.id]} />
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10]}
          />
        </>
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{editId ? "Edit Client" : "Add New Client"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, sm: 8 }}>
              <TextField
                label="Company Name *"
                fullWidth size="small"
                value={form.name}
                onChange={(e) => {
                  setForm((f) => ({
                    ...f,
                    name: e.target.value,
                    slug: editId ? f.slug : e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""),
                  }));
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Slug / ID *"
                fullWidth size="small"
                value={form.slug}
                disabled={!!editId}
                onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value.toLowerCase() }))}
                helperText={editId ? "Cannot change after creation" : "Used as Firestore path"}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select label="Status" value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as Client["status"] }))}>
                  <MenuItem value="trial">Trial</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="suspended">Suspended</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Plan</InputLabel>
                <Select label="Plan" value={form.plan} onChange={(e) => setForm((f) => ({ ...f, plan: e.target.value as Client["plan"] }))}>
                  <MenuItem value="starter">Starter</MenuItem>
                  <MenuItem value="pro">Pro</MenuItem>
                  <MenuItem value="enterprise">Enterprise</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Industry" fullWidth size="small" value={form.industry} onChange={(e) => setForm((f) => ({ ...f, industry: e.target.value }))} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Timezone" fullWidth size="small" value={form.timezone} onChange={(e) => setForm((f) => ({ ...f, timezone: e.target.value }))} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Contact Email" fullWidth size="small" type="email" value={form.contactEmail} onChange={(e) => setForm((f) => ({ ...f, contactEmail: e.target.value }))} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Contact Phone" fullWidth size="small" value={form.contactPhone} onChange={(e) => setForm((f) => ({ ...f, contactPhone: e.target.value }))} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField label="Address" fullWidth size="small" multiline rows={2} value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : editId ? "Save Changes" : "Create Client"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle sx={{ fontWeight: 700, color: "error.main" }}>Delete Client?</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This only removes the client metadata document. Subcollection data (employees, attendance, etc.)
            must be deleted separately via the Data Explorer or Firebase console.
          </Alert>
          <Typography>
            Delete <strong>{deleteConfirm?.name}</strong> (<code>{deleteConfirm?.slug}</code>)?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={deleteClient}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Configure Dialog */}
      {configureClient && (
        <ConfigureDialog
          client={configureClient}
          onClose={() => setConfigureClient(null)}
          onSaved={() => { setConfigureClient(null); load(); toast("Configuration saved"); }}
          toast={toast}
        />
      )}
    </Box>
  );
}

// ── Client Detail Panel (inline expand) ───────────────────────────────────

function ClientDetailPanel({ client, stats }: { client: Client; stats?: { employees: number; branches: number } }) {
  return (
    <Box>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Typography variant="caption" color="text.secondary">Contact</Typography>
          <Typography variant="body2">{client.contactEmail || "—"}</Typography>
          <Typography variant="body2">{client.contactPhone || "—"}</Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Typography variant="caption" color="text.secondary">Address</Typography>
          <Typography variant="body2">{client.address || "—"}</Typography>
        </Grid>
        <Grid size={{ xs: 6, sm: 3, md: 2 }}>
          <Typography variant="caption" color="text.secondary">Employees</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {stats ? stats.employees : <CircularProgress size={14} />}
          </Typography>
        </Grid>
        <Grid size={{ xs: 6, sm: 3, md: 2 }}>
          <Typography variant="caption" color="text.secondary">Branches</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {stats ? stats.branches : <CircularProgress size={14} />}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Typography variant="caption" color="text.secondary">Firestore Path</Typography>
          <Typography variant="caption" sx={{ fontFamily: "monospace", display: "block" }}>
            clients/{client.id}/
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
}

// ── Configure Dialog ──────────────────────────────────────────────────────

function ConfigureDialog({
  client,
  onClose,
  onSaved,
  toast,
}: {
  client: Client;
  onClose: () => void;
  onSaved: () => void;
  toast: (m: string, s?: "success" | "error") => void;
}) {
  const [config, setConfig] = useState<ClientConfig>(() => ({
    timesheetEditDays: client.timesheetEditDays ?? 7,
    employeeLimit: client.employeeLimit ?? 0,
    branchLimit: client.branchLimit ?? 0,
    features: {
      payroll: client.features?.payroll !== false,
      leaves: client.features?.leaves !== false,
      reports: client.features?.reports !== false,
      advances: client.features?.advances !== false,
    },
  }));
  const [saving, setSaving] = useState(false);

  const setFeature = (key: keyof ClientFeatures, val: boolean) =>
    setConfig((c) => ({ ...c, features: { ...c.features, [key]: val } }));

  const save = async () => {
    if (config.timesheetEditDays < 1 || config.timesheetEditDays > 365) {
      toast("Timesheet edit window must be between 1 and 365 days", "error");
      return;
    }
    setSaving(true);
    try {
      await updateDoc(doc(db, "clients", client.id), {
        timesheetEditDays: config.timesheetEditDays,
        employeeLimit: config.employeeLimit,
        branchLimit: config.branchLimit,
        features: config.features,
        updatedAt: Timestamp.now(),
      });
      await logPlatformAudit("client-configured", client.id, "super_admin", {
        timesheetEditDays: config.timesheetEditDays,
        employeeLimit: config.employeeLimit,
        branchLimit: config.branchLimit,
        features: config.features,
      });
      onSaved();
    } catch (e: unknown) {
      toast("Save failed: " + (e instanceof Error ? e.message : "unknown"), "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Configure — {client.name}</DialogTitle>
      <DialogContent>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, mt: 1 }}>Timesheet</Typography>
        <TextField
          label="Timesheet Edit Window (days)"
          type="number"
          size="small"
          fullWidth
          value={config.timesheetEditDays}
          onChange={(e) => setConfig((c) => ({ ...c, timesheetEditDays: Math.max(1, parseInt(e.target.value) || 1) }))}
          helperText="How many days in the past users can create or edit entries (default: 7)"
          inputProps={{ min: 1, max: 365 }}
          sx={{ mb: 2 }}
        />

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Limits</Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Max Employees"
              type="number"
              size="small"
              fullWidth
              value={config.employeeLimit}
              onChange={(e) => setConfig((c) => ({ ...c, employeeLimit: Math.max(0, parseInt(e.target.value) || 0) }))}
              helperText="0 = unlimited"
              inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Max Branches"
              type="number"
              size="small"
              fullWidth
              value={config.branchLimit}
              onChange={(e) => setConfig((c) => ({ ...c, branchLimit: Math.max(0, parseInt(e.target.value) || 0) }))}
              helperText="0 = unlimited"
              inputProps={{ min: 0 }}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Features</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
          Disable features to hide them from this client's navigation
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          <FormControlLabel
            control={<Switch checked={config.features.payroll} onChange={(e) => setFeature("payroll", e.target.checked)} size="small" />}
            label="Payroll"
          />
          <FormControlLabel
            control={<Switch checked={config.features.leaves} onChange={(e) => setFeature("leaves", e.target.checked)} size="small" />}
            label="Leaves"
          />
          <FormControlLabel
            control={<Switch checked={config.features.reports} onChange={(e) => setFeature("reports", e.target.checked)} size="small" />}
            label="Reports"
          />
          <FormControlLabel
            control={<Switch checked={config.features.advances} onChange={(e) => setFeature("advances", e.target.checked)} size="small" />}
            label="Advances"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? <CircularProgress size={18} /> : "Save Configuration"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Users Tab ─────────────────────────────────────────────────────────────

function UsersTab({ toast }: { toast: (m: string, s?: "success" | "error") => void }) {
  const [clients, setClients] = useState<Array<{ id: string; name: string }>>([]);
  const [users, setUsers] = useState<ClientUser[]>([]);
  const [selectedClient, setSelectedClient] = useState<string>("__all__");
  const [loading, setLoading] = useState(false);
  const [provisionOpen, setProvisionOpen] = useState(false);
  const [provForm, setProvForm] = useState({ email: "", clientId: "", role: "receptionist", password: "" });
  const [provisioning, setProvisioning] = useState(false);
  const [page, setPage] = useState(0);

  useEffect(() => {
    getDocs(collection(db, "clients")).then((snap) => {
      setClients(snap.docs.map((d) => ({ id: d.id, name: (d.data().name as string) || d.id })));
    }).catch(() => {});
  }, []);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const fns = getFunctions(undefined, "asia-south1");
      const fn = httpsCallable<{ clientId?: string }, { users: ClientUser[] }>(fns, "listClientUsers");
      const res = await fn({ clientId: selectedClient === "__all__" ? undefined : selectedClient });
      setUsers(res.data.users || []);
    } catch {
      // Fall back to empty — function may not be deployed yet
      setUsers([]);
      toast("Could not load users via cloud function. Deploy functions first.", "error");
    } finally {
      setLoading(false);
    }
  }, [selectedClient, toast]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const provision = async () => {
    if (!provForm.email || !provForm.clientId || !provForm.role) {
      toast("All fields are required", "error"); return;
    }
    setProvisioning(true);
    try {
      const fns = getFunctions(undefined, "asia-south1");
      const fn = httpsCallable(fns, "provisionUser");
      await fn({ email: provForm.email, clientId: provForm.clientId, role: provForm.role, password: provForm.password || undefined });
      await logPlatformAudit("user-provisioned", provForm.clientId, "super_admin", { email: provForm.email, role: provForm.role });
      toast("User provisioned successfully");
      setProvisionOpen(false);
      setProvForm({ email: "", clientId: "", role: "receptionist", password: "" });
      loadUsers();
    } catch (e: unknown) {
      toast("Provisioning failed: " + (e instanceof Error ? e.message : "unknown"), "error");
    } finally {
      setProvisioning(false);
    }
  };

  const paginated = users.slice(page * 10, page * 10 + 10);

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 1 }}>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filter by Client</InputLabel>
            <Select
              label="Filter by Client"
              value={selectedClient}
              onChange={(e) => { setSelectedClient(e.target.value); setPage(0); }}
            >
              <MenuItem value="__all__">All Clients</MenuItem>
              {clients.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Button size="small" startIcon={<RefreshIcon />} onClick={loadUsers}>Refresh</Button>
        </Box>
        <Button variant="contained" size="small" startIcon={<PersonAddIcon />} onClick={() => setProvisionOpen(true)}>
          Provision User
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress /></Box>
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ "& th": { fontWeight: 700, bgcolor: "action.hover" } }}>
                  <TableCell>Email</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginated.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={99} align="center" sx={{ py: 3, color: "text.secondary" }}>
                      {users.length === 0 ? "No users found. Deploy Cloud Functions to enable user management." : "No users on this page."}
                    </TableCell>
                  </TableRow>
                ) : (
                  paginated.map((u) => (
                    <TableRow key={u.uid} hover>
                      <TableCell>
                        <Typography variant="body2">{u.email}</Typography>
                        {u.displayName && <Typography variant="caption" color="text.secondary">{u.displayName}</Typography>}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{u.clientName || u.clientId || "—"}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={u.role === "admin" ? "Admin" : u.role === "receptionist" ? "Front Desk" : u.role}
                          size="small"
                          color={u.role === "admin" ? "primary" : u.role === "manager" ? "secondary" : "default"}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip label={u.disabled ? "Disabled" : "Active"} size="small" color={u.disabled ? "error" : "success"} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{u.createdAt ? dayjs(u.createdAt).format("DD/MMM/YYYY") : "—"}</Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={users.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={10}
            rowsPerPageOptions={[10]}
          />
        </>
      )}

      {/* Provision Dialog */}
      <Dialog open={provisionOpen} onClose={() => setProvisionOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Provision New User</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="Email *" size="small" fullWidth type="email"
              value={provForm.email} onChange={(e) => setProvForm((f) => ({ ...f, email: e.target.value }))}
            />
            <TextField
              label="Temporary Password" size="small" fullWidth type="password"
              value={provForm.password} onChange={(e) => setProvForm((f) => ({ ...f, password: e.target.value }))}
              helperText="Leave blank to auto-generate"
            />
            <FormControl fullWidth size="small">
              <InputLabel>Client *</InputLabel>
              <Select label="Client *" value={provForm.clientId} onChange={(e) => setProvForm((f) => ({ ...f, clientId: e.target.value }))}>
                {clients.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Role *</InputLabel>
              <Select label="Role *" value={provForm.role} onChange={(e) => setProvForm((f) => ({ ...f, role: e.target.value }))}>
                <MenuItem value="receptionist">Front Desk</MenuItem>
                <MenuItem value="manager">Manager</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setProvisionOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={provision} disabled={provisioning}>
            {provisioning ? <CircularProgress size={18} /> : "Provision"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Data Explorer Tab ─────────────────────────────────────────────────────

const COLLECTIONS = ["employees", "branches", "attendance", "leaveRequests", "payrollRuns", "config", "allowanceTypes", "publicHolidays", "advances", "changeRequests"];

function DataExplorerTab() {
  const [clients, setClients] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [selectedCollection, setSelectedCollection] = useState("employees");
  const [docs, setDocs] = useState<Array<{ id: string } & Record<string, unknown>>>([]);
  const [loading, setLoading] = useState(false);
  const [columns, setColumns] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [error, setError] = useState("");

  useEffect(() => {
    getDocs(collection(db, "clients")).then((snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, name: (d.data().name as string) || d.id }));
      setClients(list);
      if (list.length > 0) setSelectedClient(list[0].id);
    }).catch(() => {});
  }, []);

  const loadData = async () => {
    if (!selectedClient) return;
    setLoading(true);
    setError("");
    setPage(0);
    try {
      const snap = await getDocs(query(
        collection(db, "clients", selectedClient, selectedCollection),
        limit(200)
      ));
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() as Record<string, unknown> }));
      setDocs(rows);
      if (rows.length > 0) {
        const allKeys = new Set<string>();
        rows.forEach((r) => Object.keys(r).forEach((k) => allKeys.add(k)));
        setColumns(["id", ...Array.from(allKeys).filter((k) => k !== "id").slice(0, 8)]);
      } else {
        setColumns(["id"]);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to load data");
      setDocs([]);
    } finally {
      setLoading(false);
    }
  };

  const formatValue = (val: unknown): string => {
    if (val === null || val === undefined) return "—";
    if (val instanceof Timestamp) return dayjs(val.toDate()).format("DD/MMM/YYYY HH:mm");
    if (typeof val === "object") return JSON.stringify(val).slice(0, 60);
    return String(val);
  };

  const paginated = docs.slice(page * 25, page * 25 + 25);

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 1.5, mb: 2, flexWrap: "wrap", alignItems: "flex-end" }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Client</InputLabel>
          <Select label="Client" value={selectedClient} onChange={(e) => setSelectedClient(e.target.value)}>
            {clients.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Collection</InputLabel>
          <Select label="Collection" value={selectedCollection} onChange={(e) => setSelectedCollection(e.target.value)}>
            {COLLECTIONS.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </Select>
        </FormControl>
        <Button variant="contained" size="small" onClick={loadData} disabled={!selectedClient || loading}>
          {loading ? <CircularProgress size={16} /> : "Load Data"}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {docs.length > 0 && (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
            Showing up to 200 documents from clients/{selectedClient}/{selectedCollection} — first 8 fields visible
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ "& th": { fontWeight: 700, bgcolor: "action.hover" } }}>
                  {columns.map((c) => <TableCell key={c}>{c}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginated.map((row) => (
                  <TableRow key={row.id} hover>
                    {columns.map((c) => (
                      <TableCell key={c}>
                        <Typography variant="caption" sx={{ fontFamily: c === "id" ? "monospace" : undefined }}>
                          {formatValue(row[c])}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={docs.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={25}
            rowsPerPageOptions={[25]}
          />
        </>
      )}

      {!loading && docs.length === 0 && selectedClient && (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">
            Click "Load Data" to browse the selected collection. Run the migration script first if data is empty.
          </Typography>
        </Paper>
      )}
    </Box>
  );
}

// ── Audit Log Tab ─────────────────────────────────────────────────────────

function AuditLogTab() {
  const [logs, setLogs] = useState<PlatformAuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);

  useEffect(() => {
    getDocs(query(collection(db, "platformAuditLog"), orderBy("timestamp", "desc"), limit(200)))
      .then((snap) => {
        setLogs(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<PlatformAuditEntry, "id">) })));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const paginated = logs.slice(page * 25, page * 25 + 25);

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Platform Audit Log</Typography>
        <Typography variant="caption" color="text.secondary">Last 200 entries</Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress /></Box>
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ "& th": { fontWeight: 700, bgcolor: "action.hover" } }}>
                  <TableCell>Timestamp</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Performed By</TableCell>
                  <TableCell>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginated.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={99} align="center" sx={{ py: 3, color: "text.secondary" }}>
                      No audit entries yet
                    </TableCell>
                  </TableRow>
                ) : (
                  paginated.map((log) => (
                    <TableRow key={log.id} hover>
                      <TableCell>
                        <Typography variant="caption">
                          {log.timestamp ? dayjs(log.timestamp.toDate()).format("DD/MMM/YYYY HH:mm:ss") : "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={log.action} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ fontFamily: "monospace" }}>{log.clientId || "—"}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{log.performedBy || "—"}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{JSON.stringify(log.details || {}).slice(0, 80)}</Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={logs.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={25}
            rowsPerPageOptions={[25]}
          />
        </>
      )}
    </Box>
  );
}
